import React, { useState, useEffect } from 'react';
import { amplifyApi } from '../../services/amplifyApi';
import {
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  DollarSign,
  Filter,
  Edit2,
  Check,
  X,
  Percent,
  Settings
} from 'lucide-react';

function CommissionsTab() {
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Commission rates state
  const [ratesLoading, setRatesLoading] = useState(true);
  const [rates, setRates] = useState(null);
  const [editingRate, setEditingRate] = useState(null); // 'creator' | 'referral' | 'minPayout' | null
  const [editValue, setEditValue] = useState('');
  const [rateSaving, setRateSaving] = useState(false);

  useEffect(() => {
    loadCommissions();
    loadCommissionRates();
  }, [statusFilter]);

  const loadCommissionRates = async () => {
    setRatesLoading(true);
    try {
      const response = await amplifyApi.getAdminCommissionRates();
      if (response.success) {
        setRates(response.rates);
      }
    } catch (err) {
      console.error('Error loading commission rates:', err);
    }
    setRatesLoading(false);
  };

  const startEditingRate = (rateType) => {
    if (!rates) return;
    setEditingRate(rateType);
    if (rateType === 'creator') {
      setEditValue(rates.creator?.percent?.toString() || '10');
    } else if (rateType === 'referral') {
      setEditValue(rates.referral?.percent?.toString() || '5');
    } else if (rateType === 'minPayout') {
      setEditValue(rates.minPayout?.amount?.toString() || '25');
    }
  };

  const cancelEditingRate = () => {
    setEditingRate(null);
    setEditValue('');
  };

  const saveRate = async () => {
    if (!editingRate || !editValue) return;

    setRateSaving(true);
    setError(null);
    setSuccess(null);

    try {
      let configId, body;
      if (editingRate === 'creator') {
        configId = 'creator-rate';
        body = { commissionPercent: parseFloat(editValue) };
      } else if (editingRate === 'referral') {
        configId = 'referral-rate';
        body = { commissionPercent: parseFloat(editValue) };
      } else if (editingRate === 'minPayout') {
        configId = 'min-payout';
        body = { minPayoutAmount: parseFloat(editValue) };
      }

      const response = await amplifyApi.updateCommissionRate(configId, body);
      if (response.success) {
        setSuccess(`Commission rate updated successfully`);
        setEditingRate(null);
        setEditValue('');
        loadCommissionRates();
      } else {
        setError(response.error || 'Failed to update rate');
      }
    } catch (err) {
      setError(err.message || 'Failed to update rate');
    }

    setRateSaving(false);
  };

  const loadCommissions = async () => {
    setLoading(true);
    setError(null);

    const status = statusFilter === 'all' ? null : statusFilter;
    const response = await amplifyApi.getCommissionLedger(status);
    if (response.success) {
      setCommissions(response.commissions || []);
    } else {
      setError(response.error || 'Failed to load commissions');
    }

    setLoading(false);
  };

  const handleUpdateStatus = async (commissionId, newStatus) => {
    setActionLoading(commissionId);
    setError(null);
    setSuccess(null);

    const response = await amplifyApi.updateCommissionStatus(commissionId, newStatus);
    if (response.success) {
      setSuccess(`Commission status updated to ${newStatus}`);
      loadCommissions();
    } else {
      setError(response.error || 'Failed to update commission status');
    }

    setActionLoading(null);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Paid
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
            {status}
          </span>
        );
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'creator':
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
            Creator
          </span>
        );
      case 'affiliate':
        return (
          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
            Affiliate
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs font-medium">
            {type}
          </span>
        );
    }
  };

  // Calculate totals
  const totals = commissions.reduce(
    (acc, c) => {
      acc.total += c.amount || 0;
      if (c.status === 'pending') acc.pending += c.amount || 0;
      if (c.status === 'paid') acc.paid += c.amount || 0;
      return acc;
    },
    { total: 0, pending: 0, paid: 0 }
  );

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">Loading commissions...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Commission Rates Configuration */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <Settings className="w-5 h-5 text-slate-500" />
          <h3 className="font-semibold text-slate-900">Commission Rates Configuration</h3>
        </div>
        <div className="p-4">
          {ratesLoading ? (
            <div className="flex items-center gap-2 py-4">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span className="text-slate-500">Loading rates...</span>
            </div>
          ) : rates ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Creator Commission */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Creator Commission</span>
                  {editingRate !== 'creator' && (
                    <button
                      onClick={() => startEditingRate('creator')}
                      className="p-1 text-slate-400 hover:text-blue-600 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {editingRate === 'creator' ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-20 px-2 py-1 border border-slate-300 rounded text-right focus:outline-none focus:border-blue-500"
                    />
                    <Percent className="w-4 h-4 text-slate-400" />
                    <button
                      onClick={saveRate}
                      disabled={rateSaving}
                      className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                    >
                      {rateSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={cancelEditingRate}
                      className="p-1 text-slate-400 hover:text-slate-600 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="text-2xl font-bold text-slate-900">
                    {rates.creator?.percent ?? 10}%
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  Earned when creator's product is sold
                </p>
              </div>

              {/* Referral Commission */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Referral Commission</span>
                  {editingRate !== 'referral' && (
                    <button
                      onClick={() => startEditingRate('referral')}
                      className="p-1 text-slate-400 hover:text-blue-600 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {editingRate === 'referral' ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-20 px-2 py-1 border border-slate-300 rounded text-right focus:outline-none focus:border-blue-500"
                    />
                    <Percent className="w-4 h-4 text-slate-400" />
                    <button
                      onClick={saveRate}
                      disabled={rateSaving}
                      className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                    >
                      {rateSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={cancelEditingRate}
                      className="p-1 text-slate-400 hover:text-slate-600 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="text-2xl font-bold text-slate-900">
                    {rates.referral?.percent ?? 5}%
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  Earned when shared product link leads to sale
                </p>
              </div>

              {/* Minimum Payout */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Minimum Payout</span>
                  {editingRate !== 'minPayout' && (
                    <button
                      onClick={() => startEditingRate('minPayout')}
                      className="p-1 text-slate-400 hover:text-blue-600 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {editingRate === 'minPayout' ? (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-400" />
                    <input
                      type="number"
                      min="0"
                      step="5"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-24 px-2 py-1 border border-slate-300 rounded text-right focus:outline-none focus:border-blue-500"
                    />
                    <button
                      onClick={saveRate}
                      disabled={rateSaving}
                      className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                    >
                      {rateSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={cancelEditingRate}
                      className="p-1 text-slate-400 hover:text-slate-600 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="text-2xl font-bold text-slate-900">
                    ${rates.minPayout?.amount ?? 25}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  Minimum balance required to request payout
                </p>
              </div>
            </div>
          ) : (
            <p className="text-slate-500">Unable to load commission rates</p>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Commission Ledger</h2>
        <button
          onClick={loadCommissions}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-lg p-4">
          <div className="text-sm text-slate-500 mb-1">Total Commissions</div>
          <div className="text-2xl font-bold text-slate-900">${totals.total.toFixed(2)}</div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="text-sm text-yellow-700 mb-1">Pending</div>
          <div className="text-2xl font-bold text-yellow-700">${totals.pending.toFixed(2)}</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm text-green-700 mb-1">Paid Out</div>
          <div className="text-2xl font-bold text-green-700">${totals.paid.toFixed(2)}</div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-slate-400" />
        <span className="text-sm text-slate-600">Filter by status:</span>
        <div className="flex gap-2">
          {['all', 'pending', 'paid', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                statusFilter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Commission Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">User</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Type</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Order</th>
              <th className="text-right px-4 py-3 font-medium text-slate-600">Amount</th>
              <th className="text-center px-4 py-3 font-medium text-slate-600">Status</th>
              <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {commissions.map((commission) => (
              <tr key={commission.commissionId} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-500">
                  {commission.createdAt
                    ? new Date(commission.createdAt).toLocaleDateString()
                    : 'N/A'}
                </td>
                <td className="px-4 py-3">
                  <div>
                    <div className="font-medium text-slate-900">
                      {commission.userEmail || commission.userId?.substring(0, 12) + '...'}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {getTypeBadge(commission.type)}
                </td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs">
                  {commission.orderId?.substring(0, 12) || 'N/A'}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-semibold text-slate-900 flex items-center justify-end gap-1">
                    <DollarSign className="w-3 h-3 text-slate-400" />
                    {(commission.amount || 0).toFixed(2)}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  {getStatusBadge(commission.status)}
                </td>
                <td className="px-4 py-3 text-right">
                  {commission.status === 'pending' && (
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleUpdateStatus(commission.commissionId, 'paid')}
                        disabled={actionLoading === commission.commissionId}
                        className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                      >
                        {actionLoading === commission.commissionId ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          'Mark Paid'
                        )}
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(commission.commissionId, 'cancelled')}
                        disabled={actionLoading === commission.commissionId}
                        className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {commission.status !== 'pending' && (
                    <span className="text-xs text-slate-400">-</span>
                  )}
                </td>
              </tr>
            ))}
            {commissions.length === 0 && (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                  No commissions found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CommissionsTab;
